import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {enqueueSnackbar} from 'notistack';

import {refreshBatch} from '../api';
import {
  IMAGE_EDITOR_CONTAINS_IDS,
  IMAGE_EDITOR_IDS_ADDED,
  IMAGE_EDITOR_LOCAL_STORAGE_KEY,
  IMAGE_EDITOR_NOT_AN_IMAGE,
  IMAGE_EDITOR_NOT_IMAGES,
  PAGE_SIZE,
} from '../constants';
import {ImageEditorContextValue, SearchAssetInformationDto} from '../models';
import {useAuth} from './auth';
import {isImage} from '../utils';
import {getValue, setValue} from '../utils/localStorage';
import {isPresent} from '../utils/utils';

const noop = () => {};

const ImageEditorContext = React.createContext<ImageEditorContextValue>({
  addImage: noop,
  addImages: noop,
  removeImage: noop,
  isInImageEditor: () => false,
  imageEditorCount: 0,
  loadImageEditorAssets: noop,
  loading: false,
  imageEditorAssets: [],
  activeAsset: null,
  setActiveAsset: noop,
});

export const useImageEditor = () => useContext(ImageEditorContext);

/**
 * Kontext für die Auswahl der Bilder in der Bildbearbeitung.
 *
 * Verantwortlich ausschließlich für die Asset-Auswahl (Hinzufügen, Entfernen,
 * Laden). Der Bearbeitungs-/Formularzustand liegt im `ImageQueryFormProvider`.
 */
export const ImageEditorContextProvider = (props: {children: ReactNode}) => {
  const {token, forceLogout} = useAuth();
  const [assetMap, setAssetMap] = useState<
    Map<string, SearchAssetInformationDto | undefined>
  >(new Map());

  const [activeAsset, setActiveAsset] =
    useState<SearchAssetInformationDto | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const loadAssetsControllerRef = useRef<AbortController | null>(null);

  const addImage = useCallback((item: SearchAssetInformationDto) => {
    if (!isImage(item)) {
      enqueueSnackbar(IMAGE_EDITOR_NOT_AN_IMAGE, {variant: 'warning'});
      return;
    }

    setAssetMap(prev => {
      const next = new Map(prev).set(item.id, item);
      setValue(
        IMAGE_EDITOR_LOCAL_STORAGE_KEY,
        JSON.stringify(Array.from(next.keys())),
      );
      return next;
    });
  }, []);

  const addImages = useCallback((items: SearchAssetInformationDto[]) => {
    setAssetMap(prev => {
      if (items.every(item => prev.has(item.id))) {
        enqueueSnackbar(IMAGE_EDITOR_CONTAINS_IDS, {variant: 'warning'});
        return prev;
      }
      if (items.every(item => !isImage(item))) {
        enqueueSnackbar(IMAGE_EDITOR_NOT_IMAGES, {variant: 'warning'});
        return prev;
      }

      const next = new Map(prev);
      items.filter(isImage).forEach(item => next.set(item.id, item));
      setValue(
        IMAGE_EDITOR_LOCAL_STORAGE_KEY,
        JSON.stringify(Array.from(next.keys())),
      );

      enqueueSnackbar(IMAGE_EDITOR_IDS_ADDED, {variant: 'success'});
      return next;
    });
  }, []);

  const removeImage = useCallback((item: SearchAssetInformationDto) => {
    setAssetMap(prev => {
      const next = new Map(prev);
      next.delete(item.id);
      setValue(
        IMAGE_EDITOR_LOCAL_STORAGE_KEY,
        JSON.stringify(Array.from(next.keys())),
      );
      return next;
    });
  }, []);

  const loadLocalImageEditorAssetIds = useCallback((): string[] => {
    const rawAssetIds = getValue(IMAGE_EDITOR_LOCAL_STORAGE_KEY);
    if (rawAssetIds) {
      try {
        return JSON.parse(rawAssetIds) as string[];
      } catch {
        // Fehler beim Parsen ignorieren
      }
    }
    return [];
  }, []);

  const isInImageEditor = useCallback(
    (item: SearchAssetInformationDto): boolean => assetMap.has(item.id),
    [assetMap],
  );

  // Reversed order damit zuletzt hinzugefügte Bilder zuerst angezeigt werden
  const imageEditorAssets = useMemo((): (
    | SearchAssetInformationDto
    | undefined
  )[] => {
    return Array.from(assetMap.values()).reverse();
  }, [assetMap]);
  const imageEditorAssetIds = useMemo((): string[] => {
    return Array.from(assetMap.keys()).reverse();
  }, [assetMap]);

  const imageEditorCount = useMemo((): number => assetMap.size, [assetMap]);

  useEffect(() => {
    if (activeAsset && !assetMap.has(activeAsset.id)) {
      setActiveAsset(null);
    }
  }, [assetMap, activeAsset]);

  // Initialisiere Map mit leeren Assets um die Anzahl richtig zu stellen
  useEffect(() => {
    const ids = loadLocalImageEditorAssetIds();
    setAssetMap(new Map(ids.map(id => [id, undefined])));
  }, [loadLocalImageEditorAssetIds]);

  const loadImageEditorAssets = useCallback(
    (page: number = 0) => {
      // Abbrechen des vorherigen Requests falls noch aktiv
      if (loadAssetsControllerRef.current) {
        loadAssetsControllerRef.current.abort();
      }
      loadAssetsControllerRef.current = new AbortController();

      setLoading(true);

      const requestAssets = imageEditorAssetIds.slice(
        page * PAGE_SIZE,
        (page + 1) * PAGE_SIZE,
      );

      if (requestAssets.length === 0) {
        setLoading(false);
        return;
      }

      refreshBatch(
        requestAssets,
        undefined,
        token,
        loadAssetsControllerRef.current.signal,
      )
        .then(response => {
          const resultItems =
            response.data as (SearchAssetInformationDto | null)[];
          const assets = new Map(
            resultItems.filter(isPresent).map(item => [item.id, item]),
          );

          const deletedAssets = resultItems
            .map((item, idx) => (!isPresent(item) ? idx : null))
            .filter(isPresent)
            .map(assetIndex => requestAssets[assetIndex]);

          setAssetMap(prev => {
            const nextAssetMap = new Map([...prev, ...assets]);

            deletedAssets.forEach(assetId => {
              nextAssetMap.delete(assetId);
            });

            setValue(
              IMAGE_EDITOR_LOCAL_STORAGE_KEY,
              JSON.stringify(Array.from(nextAssetMap.keys())),
            );

            return nextAssetMap;
          });

          setLoading(false);
        })
        .catch(
          (error: {
            code?: string;
            response?: {status?: number; data?: string};
          }) => {
            if (error?.code === 'ERR_CANCELED') {
              // Request wurde abgebrochen - kein Fehler anzeigen
              return;
            }

            switch (error?.response?.status) {
              case 401:
                forceLogout();
                break;
              default:
                enqueueSnackbar('Fehler beim Laden der Assets', {
                  variant: 'error',
                });
            }
            setLoading(false);
          },
        );
    },
    [forceLogout, token, imageEditorAssetIds],
  );

  const value: ImageEditorContextValue = useMemo(
    () => ({
      addImage,
      addImages,
      removeImage,
      isInImageEditor,
      imageEditorCount,
      loadImageEditorAssets,
      loading,
      imageEditorAssets,
      activeAsset,
      setActiveAsset,
    }),
    [
      addImage,
      addImages,
      removeImage,
      isInImageEditor,
      imageEditorCount,
      loadImageEditorAssets,
      loading,
      imageEditorAssets,
      activeAsset,
      setActiveAsset,
    ],
  );

  return (
    <ImageEditorContext.Provider value={value}>
      {props.children}
    </ImageEditorContext.Provider>
  );
};
